import autoBind from 'auto-bind'

import { AppApiBase } from 'api/core/AppApiBase'
import {
  PaymentInfo,
  TransferInfo,
  TransferTransaction,
  UserTransaction,
} from 'api/domain/transactions'
import { NewCardSchema, CardsList, PaySchema, Card } from 'api/domain/cards'
import { ActionResult, PaginatedResponse, TableParams } from 'api/domain/app'
import { PurchaseData } from 'api/domain/images'
import { paymentHeaders } from 'utils'

export class UserTransactionApi {
  constructor(protected api: AppApiBase) {
    autoBind(this)
  }

  async transferTransaction(data: TransferTransaction) {
    return this.api.post<TransferInfo>(`/transaction/transfer`, data)
  }

  async redirectConfirm(id: UserTransaction['uuid']) {
    return this.api.post<ActionResult>(`/transaction/${id}/redirect_confirm`)
  }

  async getTransactionsHistory(params?: TableParams) {
    return this.api.get<PaginatedResponse<UserTransaction>>(`/transactions`, params)
  }

  async downloadPdfReceipt(uuid: UserTransaction['uuid']) {
    return this.api.get<any>(`/transactions/${uuid}/receipt`, undefined, { responseType: 'blob' })
  }

  async downloadPdfInvoice(uuid: PurchaseData['id']) {
    return this.api.get<any>(`/purchases/${uuid}/invoice`, undefined, { responseType: 'blob' })
  }

  async getTransaction(uuid: UserTransaction['uuid']) {
    return this.api.get<UserTransaction>(`/transaction/${uuid}`)
  }

  async cancelTransaction(uuid: UserTransaction['uuid']) {
    return this.api.delete<UserTransaction>(`/transaction/${uuid}`)
  }

  async getPaymentInfo(uuid: UserTransaction['uuid']) {
    return this.api.get<PaymentInfo>(
      `/transaction/${uuid}/payment`,
      {},
      { timeout: 70 * 1000, headers: paymentHeaders() },
    )
  }

  async getCardsList(id: UserTransaction['uuid']): Promise<CardsList> {
    return this.api.get<CardsList>(`/transaction/${id}/cards`)
  }

  async addNewCard(id: UserTransaction['uuid'], data: NewCardSchema): Promise<Card> {
    const newData = {
      ...data,
      transaction_id: id,
      exp_year: data?.exp_year?.length === 2 ? `20${data?.exp_year}` : data.exp_year,
    }
    return this.api.post<Card>(`/transaction/${id}/cards`, newData)
  }

  async removeCard(id: UserTransaction['uuid'], cardId: Card['id']): Promise<ActionResult> {
    return this.api.delete<ActionResult>(`/transaction/${id}/cards/${cardId}`)
  }

  async payWithCard(id: UserTransaction['uuid'], data: PaySchema): Promise<PaymentInfo> {
    return this.api.post<PaymentInfo>(`/transaction/${id}/pay`, data, {
      timeout: 70 * 1000,
      headers: paymentHeaders(),
    })
  }
}
